/**
 * Supplement Product Info – gallery, variant sync, app-backed selling plans
 * Pack / bottle products: Select Quantity maps to bottle pack (months × 30); quantity stays 1.
 * Flavor-only products (no pack option): quantity equals the selected plan length.
 * One-time: bottle size is selectable and drives price. Prices from allocations.
 */
(function () {
  const productJsonId = (sectionId) => `SupplementProductJson-${sectionId}`;
  const allocationsJsonId = (sectionId) => `SupplementAllocationsJson-${sectionId}`;
  const PER_SERVING_SUFFIX = ' per serving';

  function getProductData(sectionId) {
    const el = document.getElementById(productJsonId(sectionId));
    if (!el) return null;
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return null;
    }
  }

  function getAllocationsData(sectionId) {
    const el = document.getElementById(allocationsJsonId(sectionId));
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  function getInventoryData(sectionId) {
    const el = document.getElementById(`SupplementInventoryJson-${sectionId}`);
    if (!el) return {};
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      return {};
    }
  }

  function findVariantByOptions(product, optionValuesByPosition) {
    if (!product?.variants?.length) return null;
    const options = product.options;
    return product.variants.find((v) => {
      return options.every((opt, i) => {
        const key = `option${i + 1}`;
        return v[key] === optionValuesByPosition[i];
      });
    });
  }

  function formatMoney(cents) {
    const amount = ((Number(cents) || 0) / 100).toFixed(2);
    const parts = amount.split('.');
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    return '$' + parts.join('.');
  }

  function formatPerServing(totalCents, servings) {
    const count = Math.max(1, Number(servings) || 1);
    const perServingCents = Math.round((Number(totalCents) || 0) / count);
    return formatMoney(perServingCents) + PER_SERVING_SUFFIX;
  }

  function formatIntervalLabel(months) {
    const m = Math.max(1, Number(months) || 1);
    return m === 1 ? 'Every 1 Month' : `Every ${m} Months`;
  }

  function extractCount(value) {
    const matches = String(value || '').match(/\b(\d+)\b/g);
    if (!matches) return null;
    for (const m of matches) {
      const n = Number(m);
      if (n >= 30 && n % 30 === 0) return n;
    }
    return null;
  }

  function getPackSizeOptionIndex(product) {
    const options = product?.options || [];
    let idx = options.findIndex((name) => {
      const n = String(name).toLowerCase();
      return n.includes('pack') || n.includes('size') || n.includes('count') || n.includes('bottle');
    });
    if (idx >= 0) return idx;
    for (let i = 0; i < options.length; i++) {
      const values = product.variants.map((v) => v[`option${i + 1}`]);
      if (values.some((v) => extractCount(v) != null)) return i;
    }
    return -1;
  }

  /** Tablet/serving count from the selected package. */
  function getSupplyCountFromVariant(product, variant) {
    if (!variant) return null;
    const packIdx = getPackSizeOptionIndex(product);
    if (packIdx >= 0) {
      const fromPack = extractCount(variant[`option${packIdx + 1}`]);
      if (fromPack != null) return fromPack;
    }
    for (let i = 1; i <= 3; i++) {
      const fromOpt = extractCount(variant[`option${i}`]);
      if (fromOpt != null) return fromOpt;
    }
    return extractCount(variant.title) || extractCount(product?.title) || null;
  }

  function countForMonths(months) {
    const m = Math.max(1, Number(months) || 1);
    return m * 30;
  }

  function findPackValueForCount(product, packIdx, count) {
    if (packIdx < 0) return null;
    const values = new Set(product.variants.map((v) => v[`option${packIdx + 1}`]));
    for (const value of values) {
      if (extractCount(value) === count) return value;
    }
    return null;
  }

  function setPackSizeLocked(container, locked) {
    if (locked) container.setAttribute('data-pack-size-locked', '');
    else container.removeAttribute('data-pack-size-locked');
  }

  function syncRadiosToVariant(container, product, variant) {
    if (!variant || !product?.options) return;
    product.options.forEach((optName, i) => {
      const value = variant[`option${i + 1}`];
      if (!value) return;
      const inputs = container.querySelectorAll(
        `[data-variant-selects] input[data-option-input][name*="${String(optName).replace(/"/g, '')}"]`
      );
      inputs.forEach((input) => {
        input.checked = input.value === value;
      });
    });
  }

  /**
   * Flavor-only: no pack/bottle option from the existing detector.
   * Pack products keep findVariantForPlan's current body.
   */
  function isFlavorOnlySubscription(product) {
    return getPackSizeOptionIndex(product) < 0;
  }

  function isPrepaidAllocation(allocation) {
    if (!allocation) return false;
    const price = Number(allocation.price);
    const perDelivery = Number(allocation.per_delivery_price);
    return perDelivery > 0 && perDelivery !== price;
  }

  function variantFromCheckedOptions(container, product) {
    if (!product?.options?.length) {
      return getActiveVariant(container) || product?.variants?.[0] || null;
    }
    const optionValues = product.options.map((optName) => {
      const input = container.querySelector(
        `[data-variant-selects] input[data-option-input][name*="${String(optName).replace(/"/g, '')}"]:checked`
      );
      return input ? input.value : null;
    });
    if (optionValues.some((value) => value == null)) {
      return getActiveVariant(container) || product.variants?.[0] || null;
    }
    return (
      findVariantByOptions(product, optionValues) ||
      getActiveVariant(container) ||
      product.variants?.[0] ||
      null
    );
  }

  /** Selected flavor, quantity = plan months. Prepaid plans stay at quantity 1. */
  function resolveFlavorQuantityPlan(container, product, months, sellingPlanId) {
    const variant = variantFromCheckedOptions(container, product);
    const allocation = sellingPlanId ? getAllocationPrice(container, variant, sellingPlanId) : null;
    const quantity = isPrepaidAllocation(allocation) ? 1 : Math.max(1, Number(months) || 1);
    return { variant, quantity, supplyCount: null };
  }

  function trackedStockMax(container, variant) {
    if (!variant) return null;
    const info = getInventoryData(container.dataset.sectionId)[String(variant.id)];
    if (!info || info.inventory_management == null || info.inventory_management === '') return null;
    if (info.inventory_policy === 'continue') return null;
    return Math.max(0, Number(info.inventory_quantity) || 0);
  }

  function flavorQuantityInStock(container, variant, quantity) {
    if (!variant) return false;
    const info = getInventoryData(container.dataset.sectionId)[String(variant.id)];
    if (!info || info.inventory_management == null || info.inventory_management === '') {
      return variant.available !== false;
    }
    if (info.inventory_policy === 'continue') return true;
    return Number(info.inventory_quantity) >= Math.max(1, Number(quantity) || 1);
  }

  /**
   * Subscribe: map plan months → bottle pack (months × 30). Qty always 1.
   * If no matching pack exists, keep the current/fallback variant (no months multiplier).
   */
  function findVariantForPlan(container, product, months, sellingPlanId) {
    if (isFlavorOnlySubscription(product)) {
      return resolveFlavorQuantityPlan(container, product, months, sellingPlanId);
    }

    const count = countForMonths(months);
    const packIdx = getPackSizeOptionIndex(product);
    const packValue = findPackValueForCount(product, packIdx, count);

    if (packIdx >= 0 && packValue) {
      const optionValues = (product.options || []).map((optName, i) => {
        if (i === packIdx) return packValue;
        const input = container.querySelector(
          `[data-variant-selects] input[data-option-input][name*="${String(optName).replace(/"/g, '')}"]:checked`
        );
        if (input) return input.value;
        const active = getActiveVariant(container);
        return active ? active[`option${i + 1}`] : null;
      });

      let variant = findVariantByOptions(product, optionValues);
      if (!variant) {
        variant = product.variants.find((v) => extractCount(v[`option${packIdx + 1}`]) === count) || null;
      }
      if (variant && sellingPlanId && !getAllocationPrice(container, variant, sellingPlanId)) {
        const withAllocation = product.variants.find(
          (v) =>
            extractCount(v[`option${packIdx + 1}`]) === count && getAllocationPrice(container, v, sellingPlanId)
        );
        if (withAllocation) variant = withAllocation;
      }
      if (variant) {
        return { variant, quantity: 1, supplyCount: count };
      }
    }

    const fallback = getActiveVariant(container) || product.variants?.[0] || null;
    return {
      variant: fallback,
      quantity: 1,
      supplyCount: getSupplyCountFromVariant(product, fallback) || count,
    };
  }

  function getPlanMonthsFromCard(card) {
    if (!card) return 1;
    const fromData = Number(card.getAttribute('data-plan-months'));
    if (fromData > 0) return fromData;
    const intervalText =
      card.querySelector('[data-plan-interval]')?.textContent ||
      card.querySelector('.supplement-product__plan-interval')?.textContent ||
      '';
    const match = String(intervalText).match(/(\d+)/);
    if (match) return Math.max(1, Number(match[1]));
    return 1;
  }

  function getCheckedPlanRadio(container) {
    const planSelector = container.querySelector('[data-plan-selector]');
    if (!planSelector) return null;
    return planSelector.querySelector('[data-plan-radio]:checked');
  }

  function getSelectedPlanCard(container) {
    const checked = getCheckedPlanRadio(container);
    if (checked) return checked.closest('[data-plan-card]');
    return container.querySelector('[data-plan-card].is-selected');
  }

  function getSelectedSellingPlanId(container) {
    const checkedRadio = getCheckedPlanRadio(container);
    if (!checkedRadio) return '';
    return (checkedRadio.getAttribute('data-selling-plan-id') || checkedRadio.value || '').trim();
  }

  /** Selling plan used for Subscribe & Save price display (even when Buy one time is selected). */
  function getPreviewSellingPlanId(container) {
    const selected = getSelectedSellingPlanId(container);
    if (selected) return selected;

    const lastIndex = container.dataset.lastPlanIndex;
    if (lastIndex) {
      const lastCard = container.querySelector(`[data-plan-card][data-plan-index="${lastIndex}"]`);
      const lastRadio = lastCard?.querySelector('[data-plan-radio]');
      const lastId = (lastRadio?.getAttribute('data-selling-plan-id') || lastRadio?.value || '').trim();
      if (lastId) return lastId;
    }

    const firstRadio = container.querySelector(
      '[data-plan-card]:not(.is-unavailable) [data-plan-radio], [data-plan-card] [data-plan-radio]'
    );
    return (firstRadio?.getAttribute('data-selling-plan-id') || firstRadio?.value || '').trim();
  }

  function syncSellingPlanInput(container) {
    const input = container.querySelector('[data-selling-plan]');
    if (!input) return;
    const purchaseType = getSelectedPurchaseType(container);
    if (purchaseType !== 'subscribe') {
      input.value = '';
      input.disabled = true;
      return;
    }
    input.disabled = false;
    input.value = getSelectedSellingPlanId(container);
  }

  function getAllocationPrice(container, variant, sellingPlanId) {
    if (!variant || !sellingPlanId) return null;
    const allocations = getAllocationsData(container.dataset.sectionId);
    const byVariant = allocations[String(variant.id)];
    if (!byVariant) return null;
    return byVariant[String(sellingPlanId)] || null;
  }

  function getSelectedPurchaseType(container) {
    const checked = container.querySelector('[data-purchase-type]:checked');
    return checked ? checked.value : 'subscribe';
  }

  /** Pack counts hidden from bottle-size UI / subscribe bullet (variant kept for plans). */
  const HIDDEN_PACK_COUNTS = new Set([60]);

  function isHiddenPackCount(count) {
    return count != null && HIDDEN_PACK_COUNTS.has(Number(count));
  }

  function updateSubscribeBullet(container, months, count) {
    const el = container.querySelector('[data-subscribe-bullet-supply]');
    if (!el) return;
    const product = getProductData(container.dataset.sectionId);
    if (product && isFlavorOnlySubscription(product)) {
      const units = Math.max(1, Number(container.dataset.planQuantity) || Number(months) || 1);
      const unitWord = units === 1 ? 'unit' : 'units';
      const intervalLabel = Number(months) === 1 ? 'every month' : `every ${months} months`;
      el.textContent = `${units} ${unitWord} shipped ${intervalLabel}`;
      return;
    }
    const intervalLabel = months === 1 ? 'Every Month' : `Every ${months} Months`;
    if (count != null && count > 0 && !isHiddenPackCount(count)) {
      el.textContent = `One ${count} Count Bottle Shipped ${intervalLabel}`;
    } else {
      el.textContent = `Bottle Shipped ${intervalLabel}`;
    }
  }

  /**
   * One-time: if the active pack is UI-hidden (e.g. 60), switch to a visible pack
   * (prefer 90, then 30, then any non-hidden) while keeping other options.
   */
  function ensureVisiblePackForOnetime(container) {
    if (getSelectedPurchaseType(container) !== 'onetime') return null;
    const sectionId = container.dataset.sectionId;
    const product = getProductData(sectionId);
    if (!product?.options?.length) return null;

    const variant = getActiveVariant(container);
    if (!variant) return null;
    if (!isHiddenPackCount(getSupplyCountFromVariant(product, variant))) return null;

    const packIdx = getPackSizeOptionIndex(product);
    if (packIdx < 0) return null;

    const preferredCounts = [90, 30];
    let packValue = null;
    for (const count of preferredCounts) {
      packValue = findPackValueForCount(product, packIdx, count);
      if (packValue) break;
    }
    if (!packValue) {
      const values = new Set(product.variants.map((v) => v[`option${packIdx + 1}`]));
      for (const value of values) {
        if (!isHiddenPackCount(extractCount(value))) {
          packValue = value;
          break;
        }
      }
    }
    if (!packValue) return null;

    const optionValues = product.options.map((_, i) => {
      if (i === packIdx) return packValue;
      return variant[`option${i + 1}`];
    });
    const next = findVariantByOptions(product, optionValues);
    if (!next) return null;
    syncRadiosToVariant(container, product, next);
    return next;
  }

  /** Flavor-only plan label: 1-month allocation for this variant, not the multiplied term total. */
  function oneMonthSubscriptionCents(container, variant) {
    const oneMonthCard = container.querySelector('[data-plan-card][data-plan-months="1"]');
    const planId =
      oneMonthCard?.querySelector('[data-plan-radio]')?.getAttribute('data-selling-plan-id') ||
      oneMonthCard?.querySelector('[data-plan-radio]')?.value ||
      '';
    const allocation = planId ? getAllocationPrice(container, variant, planId) : null;
    if (allocation && allocation.price != null) return Number(allocation.price);
    return Number(variant?.price) || 0;
  }

  function updatePlanCardDisplays(container) {
    const sectionId = container.dataset.sectionId;
    const product = getProductData(sectionId);
    if (!product) return;

    container.querySelectorAll('[data-plan-card]').forEach((card) => {
      const months = getPlanMonthsFromCard(card);
      const sellingPlanId =
        card.querySelector('[data-plan-radio]')?.getAttribute('data-selling-plan-id') ||
        card.querySelector('[data-plan-radio]')?.value ||
        '';
      const resolved = findVariantForPlan(container, product, months, sellingPlanId);
      const variant = resolved?.variant;
      const flavorOnly = isFlavorOnlySubscription(product);
      const supplyCount = flavorOnly
        ? null
        : (resolved?.supplyCount || getSupplyCountFromVariant(product, variant));

      card.setAttribute('data-plan-months', String(months));
      if (supplyCount != null) {
        card.setAttribute('data-plan-supply-count', String(supplyCount));
      } else {
        card.removeAttribute('data-plan-supply-count');
      }

      const intervalEl = card.querySelector('[data-plan-interval]');
      if (intervalEl && !intervalEl.hasAttribute('data-plan-interval-override')) {
        intervalEl.textContent = formatIntervalLabel(months);
      }

      const priceEl = card.querySelector('[data-plan-price]');
      if (!priceEl || priceEl.hasAttribute('data-plan-price-override') || !variant) return;

      const allocation = sellingPlanId ? getAllocationPrice(container, variant, sellingPlanId) : null;
      const unitPrice = allocation ? allocation.price : variant.price;
      if (supplyCount != null && supplyCount > 0) {
        priceEl.textContent = formatPerServing(unitPrice, supplyCount);
      } else if (flavorOnly) {
        priceEl.textContent = formatPerServing(oneMonthSubscriptionCents(container, variant), 30);
      } else {
        priceEl.textContent = formatMoney(unitPrice);
      }
    });
  }

  function isPlanAvailable(container, product, card) {
    const planId =
      card.querySelector('[data-plan-radio]')?.getAttribute('data-selling-plan-id') ||
      card.querySelector('[data-plan-radio]')?.value ||
      '';
    if (!planId) return false;
    return product.variants.some((v) => Boolean(getAllocationPrice(container, v, planId)));
  }

  function refreshPlanAvailability(container) {
    const sectionId = container.dataset.sectionId;
    const product = getProductData(sectionId);
    if (!product) return;
    const flavorOnly = isFlavorOnlySubscription(product);
    container.querySelectorAll('[data-plan-card]').forEach((card) => {
      let available = isPlanAvailable(container, product, card);
      if (available && flavorOnly) {
        const planId =
          card.querySelector('[data-plan-radio]')?.getAttribute('data-selling-plan-id') ||
          card.querySelector('[data-plan-radio]')?.value ||
          '';
        const months = getPlanMonthsFromCard(card);
        const resolved = resolveFlavorQuantityPlan(container, product, months, planId);
        const allocation = getAllocationPrice(container, resolved?.variant, planId);
        available =
          Boolean(allocation) && flavorQuantityInStock(container, resolved?.variant, resolved?.quantity);
      }
      card.classList.toggle('is-unavailable', !available);
      card.hidden = !available;
      const radio = card.querySelector('[data-plan-radio]');
      if (radio) {
        radio.disabled = !available;
        if (!available) radio.checked = false;
      }
      if (!available) card.classList.remove('is-selected');
    });
  }

  /** Sync selling plan, map Select Quantity → bottle pack, lock pack UI. Qty always 1. */
  function applySelectedPlan(container) {
    const sectionId = container.dataset.sectionId;
    const product = getProductData(sectionId);
    if (!product) return null;

    setPackSizeLocked(container, true);
    refreshPlanAvailability(container);

    let card = getSelectedPlanCard(container);
    if (!card || card.classList.contains('is-unavailable')) {
      const firstAvailable = container.querySelector('[data-plan-card]:not(.is-unavailable)');
      if (!firstAvailable) return getActiveVariant(container) || product.variants?.[0] || null;
      container.querySelectorAll('[data-plan-card]').forEach((c) => c.classList.remove('is-selected'));
      firstAvailable.classList.add('is-selected');
      const radio = firstAvailable.querySelector('[data-plan-radio]');
      if (radio) radio.checked = true;
      card = firstAvailable;
    }

    const months = getPlanMonthsFromCard(card);
    const sellingPlanId = getSelectedSellingPlanId(container);
    const resolved = findVariantForPlan(container, product, months, sellingPlanId);
    const variant = resolved?.variant || getActiveVariant(container) || product.variants?.[0] || null;
    if (variant) syncRadiosToVariant(container, product, variant);

    const supplyCount = resolved?.supplyCount || getSupplyCountFromVariant(product, variant);
    const planQty = Math.max(1, Number(resolved?.quantity) || 1);
    const quantityInput = container.querySelector('[data-plan-quantity]');
    if (quantityInput) quantityInput.value = String(planQty);
    container.dataset.planQuantity = String(planQty);
    if (supplyCount != null) {
      container.dataset.planSupplyCount = String(supplyCount);
    } else {
      delete container.dataset.planSupplyCount;
    }
    syncSellingPlanInput(container);
    updateSubscribeBullet(container, months, supplyCount);
    updatePlanCardDisplays(container);
    return variant;
  }

  function clearPlanSelection(container) {
    const checked = getCheckedPlanRadio(container);
    if (checked) {
      const card = checked.closest('[data-plan-card]');
      if (card) {
        container.dataset.lastPlanIndex = card.getAttribute('data-plan-index') || '';
      }
    }
    container.querySelectorAll('[data-plan-card]').forEach((card) => {
      card.classList.remove('is-selected');
    });
    container.querySelectorAll('[data-plan-radio]').forEach((radio) => {
      radio.checked = false;
    });
    syncSellingPlanInput(container);
  }

  function selectDefaultOrFirstPlan(container) {
    refreshPlanAvailability(container);
    if (getCheckedPlanRadio(container) && !getSelectedPlanCard(container)?.classList.contains('is-unavailable')) {
      syncSellingPlanInput(container);
      return;
    }
    const cards = Array.from(container.querySelectorAll('[data-plan-card]:not(.is-unavailable)'));
    if (!cards.length) return;
    const lastIndex = container.dataset.lastPlanIndex;
    const defaultCard =
      (lastIndex &&
        cards.find((card) => card.getAttribute('data-plan-index') === lastIndex)) ||
      cards[0];
    container.querySelectorAll('[data-plan-card]').forEach((card) => card.classList.remove('is-selected'));
    defaultCard.classList.add('is-selected');
    const radio = defaultCard.querySelector('[data-plan-radio]');
    if (radio) radio.checked = true;
    syncSellingPlanInput(container);
  }

  function setPurchaseType(container, type) {
    const input = container.querySelector(`[data-purchase-type][value="${type}"]`);
    if (input) input.checked = true;
    container.querySelectorAll('[data-purchase-option]').forEach((o) => o.classList.remove('is-selected'));
    const option = container.querySelector(`[data-purchase-option="${type}"]`);
    if (option) option.classList.add('is-selected');
  }

  function getActiveVariant(container) {
    const sectionId = container.dataset.sectionId;
    const product = getProductData(sectionId);
    if (!product?.variants?.length) return null;
    const variantIdInput = container.querySelector('[data-variant-id]');
    const variantId = variantIdInput ? variantIdInput.value : null;
    return product.variants.find((v) => String(v.id) === String(variantId)) || product.variants[0];
  }

  function getOnetimeQtyInput(container) {
    return container.querySelector('[data-onetime-qty-input]');
  }

  function readOnetimeQty(container) {
    const input = getOnetimeQtyInput(container);
    const qty = Math.floor(Number(input && input.value));
    return Number.isFinite(qty) && qty > 0 ? qty : 1;
  }

  function setOnetimeQty(container, qty, max) {
    const input = getOnetimeQtyInput(container);
    let next = Math.max(1, Math.floor(Number(qty) || 1));
    if (max != null && max >= 1) next = Math.min(next, max);
    if (input) input.value = String(next);
    const dec = container.querySelector('[data-onetime-qty-dec]');
    const inc = container.querySelector('[data-onetime-qty-inc]');
    if (dec) dec.disabled = next <= 1;
    if (inc) inc.disabled = max != null && next >= max;
    return next;
  }

  function syncOnetimeQtyVisibility(container) {
    const el = container.querySelector('[data-onetime-qty]');
    if (!el) return;
    el.hidden = getSelectedPurchaseType(container) !== 'onetime';
  }

  function updatePlanQuantityAndTotals(container, variant) {
    if (!variant) return;
    const sectionId = container.dataset.sectionId;
    const product = getProductData(sectionId);
    const quantityInput = container.querySelector('[data-plan-quantity]');
    const subscribePrice = container.querySelector('[data-subscribe-price]');
    const subscribeCompare = container.querySelector('[data-subscribe-compare]');
    const onetimePrice = container.querySelector('[data-onetime-price]');
    const addToCartPrice = container.querySelector('[data-add-to-cart-price]');

    const unitRegular = variant.price;
    const purchaseType = getSelectedPurchaseType(container);
    // Cart only attaches a selling plan while subscribed; display always prefers a plan for discount UI.
    const sellingPlanIdForCart = purchaseType === 'subscribe' ? getSelectedSellingPlanId(container) : '';
    const sellingPlanIdForDisplay = sellingPlanIdForCart || getPreviewSellingPlanId(container);
    const allocation = sellingPlanIdForDisplay
      ? getAllocationPrice(container, variant, sellingPlanIdForDisplay)
      : null;
    const unitSubscribe = allocation ? allocation.price : unitRegular;
    let planQty = 1;
    let displayQty = 1;
    if (product && isFlavorOnlySubscription(product) && !isPrepaidAllocation(allocation)) {
      const previewRadio = sellingPlanIdForDisplay
        ? container.querySelector(`[data-plan-radio][data-selling-plan-id="${sellingPlanIdForDisplay}"]`)
        : null;
      const previewCard = previewRadio ? previewRadio.closest('[data-plan-card]') : getSelectedPlanCard(container);
      displayQty = getPlanMonthsFromCard(previewCard);
      if (purchaseType === 'subscribe') planQty = displayQty;
    }
    if (purchaseType === 'onetime') {
      const stockMax = trackedStockMax(container, variant);
      planQty = setOnetimeQty(container, readOnetimeQty(container), stockMax);
    }
    syncOnetimeQtyVisibility(container);

    if (quantityInput) quantityInput.value = String(planQty);
    container.dataset.planQuantity = String(planQty);
    syncSellingPlanInput(container);

    const displaySubscribe = unitSubscribe * displayQty;
    const displayRegular = unitRegular * displayQty;
    const atcDisplay = purchaseType === 'onetime' ? unitRegular * planQty : displaySubscribe;

    if (subscribePrice) subscribePrice.textContent = formatMoney(displaySubscribe);
    if (subscribeCompare) {
      if (displaySubscribe < displayRegular) {
        subscribeCompare.textContent = formatMoney(displayRegular);
        subscribeCompare.hidden = false;
      } else {
        subscribeCompare.hidden = true;
      }
    }
    if (onetimePrice) onetimePrice.textContent = formatMoney(unitRegular);
    if (addToCartPrice) addToCartPrice.textContent = formatMoney(atcDisplay);

    if (purchaseType === 'subscribe') {
      const card = getSelectedPlanCard(container);
      const months = getPlanMonthsFromCard(card);
      const supplyCount = getSupplyCountFromVariant(product, variant);
      if (supplyCount != null) {
        container.dataset.planSupplyCount = String(supplyCount);
      } else {
        delete container.dataset.planSupplyCount;
      }
      updateSubscribeBullet(container, months, supplyCount);
    }
    updatePlanCardDisplays(container);
  }

  function activateThumb(gallery, thumb) {
    const mainImage = gallery.querySelector('[data-main-image] img');
    if (!mainImage || !thumb) return;

    gallery.querySelectorAll('[data-thumb]').forEach((t) => t.classList.remove('is-active'));
    thumb.classList.add('is-active');

    const fullSrc = thumb.getAttribute('data-full-src');
    if (fullSrc) {
      mainImage.src = fullSrc;
    } else {
      const img = thumb.querySelector('img');
      if (img) mainImage.src = img.currentSrc || img.src;
    }

    const img = thumb.querySelector('img');
    if (img) mainImage.alt = img.alt || '';

    const mediaId = thumb.getAttribute('data-media-id');
    if (mediaId) mainImage.dataset.mediaId = mediaId;
  }

  function getSelectedFlavorValue(container) {
    const flavorInput = Array.from(
      container.querySelectorAll('[data-variant-selects] input[data-option-input]:checked')
    ).find((input) => {
      const name = (input.name || '').toLowerCase();
      return name.includes('flavor') || name.includes('flavour');
    });

    if (flavorInput) return flavorInput.value;

    const firstChecked = container.querySelector('[data-variant-selects] input[data-option-input]:checked');
    return firstChecked ? firstChecked.value : null;
  }

  function findThumbByFlavor(gallery, flavorValue) {
    if (!flavorValue) return null;

    const flavorDown = flavorValue.toLowerCase().trim();
    const thumbs = gallery.querySelectorAll('[data-thumb]');
    let bestThumb = null;
    let bestLen = 0;

    thumbs.forEach((thumb) => {
      const alt = (thumb.querySelector('img')?.alt || thumb.getAttribute('aria-label') || '').toLowerCase().trim();
      if (!alt) return;

      if (alt === flavorDown || flavorDown === alt) {
        bestThumb = thumb;
        bestLen = alt.length;
        return;
      }

      if (flavorDown.includes(alt) || alt.includes(flavorDown)) {
        if (alt.length > bestLen) {
          bestThumb = thumb;
          bestLen = alt.length;
        }
      }
    });

    return bestThumb;
  }

  function updateGalleryForVariant(container, variant) {
    const gallery = container.querySelector('[data-supplement-gallery]');
    if (!gallery || !variant) return;

    const mediaId = variant.featured_media?.id;
    if (mediaId) {
      const thumb = gallery.querySelector(`[data-thumb][data-media-id="${mediaId}"]`);
      if (thumb) {
        activateThumb(gallery, thumb);
        return;
      }
    }

    const flavorThumb = findThumbByFlavor(gallery, getSelectedFlavorValue(container));
    if (flavorThumb) {
      activateThumb(gallery, flavorThumb);
      return;
    }

    const imageSrc = variant.featured_media?.preview_image?.src || variant.featured_image?.src;
    const mainImage = gallery.querySelector('[data-main-image] img');
    if (imageSrc && mainImage) mainImage.src = imageSrc;
  }

  function initGallery(container) {
    const gallery = container.querySelector('[data-supplement-gallery]');
    if (!gallery) return;

    gallery.querySelectorAll('[data-thumb]').forEach((thumb) => {
      thumb.addEventListener('click', () => activateThumb(gallery, thumb));
    });
  }

  function initVariantSync(container) {
    const sectionId = container.dataset.sectionId;
    const product = getProductData(sectionId);
    if (!product) return;

    const variantInput = container.querySelector('[data-variant-id]');
    const submitBtn = container.querySelector('.supplement-product__add-btn');

    function updateUI(variant) {
      if (!variant) return;
      if (variantInput) variantInput.value = variant.id;
      updatePlanQuantityAndTotals(container, variant);
      updateGalleryForVariant(container, variant);
      if (submitBtn) {
        let canAdd = Boolean(variant.available);
        const purchaseType = getSelectedPurchaseType(container);
        if (canAdd && purchaseType === 'onetime') {
          const qty = Math.max(1, Number(container.dataset.planQuantity) || 1);
          canAdd = flavorQuantityInStock(container, variant, qty);
        } else if (canAdd && purchaseType === 'subscribe' && isFlavorOnlySubscription(product)) {
          const qty = Math.max(1, Number(container.dataset.planQuantity) || 1);
          canAdd =
            Boolean(getSelectedSellingPlanId(container)) && flavorQuantityInStock(container, variant, qty);
        }
        if (variant.available && canAdd) {
          submitBtn.removeAttribute('disabled');
        } else if (!variant.available) {
          submitBtn.setAttribute('disabled', 'disabled');
          const textSpan = submitBtn.querySelector('[data-button-text]');
          if (textSpan) textSpan.textContent = window.variantStrings?.unavailable || 'Unavailable';
        } else {
          submitBtn.setAttribute('disabled', 'disabled');
        }
      }
      syncSupplementFactsToVariant(sectionId, product, variant);
    }

    container._supplementUpdateUI = updateUI;

    function onOptionChange() {
      if (!product?.options) return;

      // Subscribe: bottle is driven by Select Quantity — re-resolve pack from the plan
      // (flavor changes still apply; pack size stays locked to the plan).
      if (getSelectedPurchaseType(container) === 'subscribe') {
        const variant = applySelectedPlan(container);
        updateUI(variant || product.variants[0]);
        return;
      }

      const optionValues = product.options.map((optName) => {
        const input = container.querySelector(
          `[data-variant-selects] input[data-option-input][name*="${optName.replace(/"/g, '')}"]:checked`
        );
        return input ? input.value : null;
      });
      const variant = findVariantByOptions(product, optionValues) || product.variants[0];
      updateUI(variant);
    }

    container.querySelectorAll('[data-variant-selects] [data-option-input]').forEach((input) => {
      input.addEventListener('change', onOptionChange);
    });

    const urlVariantId = new URLSearchParams(window.location.search).get('variant');
    const urlVariant = urlVariantId
      ? product.variants.find((v) => String(v.id) === String(urlVariantId))
      : null;

    if (urlVariant && getSelectedPurchaseType(container) === 'onetime') {
      if (variantInput) variantInput.value = urlVariant.id;
      const visible = ensureVisiblePackForOnetime(container);
      updateUI(visible || urlVariant);
    } else if (getSelectedPurchaseType(container) === 'subscribe') {
      const variant = applySelectedPlan(container);
      updateUI(variant || product.selected_or_first_available_variant || product.variants[0]);
    } else {
      const initial = urlVariant || product.selected_or_first_available_variant || product.variants[0];
      if (initial && variantInput) variantInput.value = initial.id;
      const visible = ensureVisiblePackForOnetime(container);
      updateUI(visible || initial);
    }
  }

  function initPlanCards(container) {
    const sectionId = container.dataset.sectionId;
    const product = getProductData(sectionId);

    container.querySelectorAll('[data-plan-card]').forEach((card) => {
      card.addEventListener('click', () => {
        if (card.classList.contains('is-unavailable')) return;
        if (getSelectedPurchaseType(container) === 'onetime') {
          setPurchaseType(container, 'subscribe');
        }
        setPackSizeLocked(container, true);
        container.querySelectorAll('[data-plan-card]').forEach((c) => c.classList.remove('is-selected'));
        card.classList.add('is-selected');
        const radio = card.querySelector('[data-plan-radio]');
        if (radio) radio.checked = true;
        syncSellingPlanInput(container);
        const variant = applySelectedPlan(container);
        if (container._supplementUpdateUI) {
          container._supplementUpdateUI(variant || product?.variants?.[0]);
        } else if (variant) {
          updatePlanQuantityAndTotals(container, variant);
        }
      });
    });

    refreshPlanAvailability(container);
    syncSellingPlanInput(container);
  }

  function initPurchaseOptions(container) {
    container.querySelectorAll('[data-purchase-type]').forEach((input) => {
      input.addEventListener('change', () => {
        const option = container.querySelector(`[data-purchase-option="${input.value}"]`);
        container.querySelectorAll('[data-purchase-option]').forEach((o) => o.classList.remove('is-selected'));
        if (option) option.classList.add('is-selected');

        if (input.value === 'onetime') {
          setPackSizeLocked(container, false);
          clearPlanSelection(container);
          setOnetimeQty(container, 1, null);
          container.dataset.planQuantity = '1';
          const quantityInput = container.querySelector('[data-plan-quantity]');
          if (quantityInput) quantityInput.value = '1';
          const variant = ensureVisiblePackForOnetime(container) || getActiveVariant(container);
          if (container._supplementUpdateUI) container._supplementUpdateUI(variant);
          else if (variant) updatePlanQuantityAndTotals(container, variant);
          return;
        }

        setPackSizeLocked(container, true);
        selectDefaultOrFirstPlan(container);
        const variant = applySelectedPlan(container);
        if (container._supplementUpdateUI) container._supplementUpdateUI(variant);
        else if (variant) updatePlanQuantityAndTotals(container, variant);
      });
    });
  }

  function formatDeliveryDate(date) {
    try {
      const parts = new Intl.DateTimeFormat('en-NZ', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
        timeZone: 'Pacific/Auckland',
      }).formatToParts(date);
      const get = (type) => parts.find((p) => p.type === type)?.value || '';
      const weekday = get('weekday').replace(/\.$/, '');
      const day = get('day');
      const month = get('month').replace(/\.$/, '');
      return `${weekday}, ${day}-${month}`;
    } catch (e) {
      return date.toDateString();
    }
  }

  function addDaysFromToday(days) {
    const now = new Date();
    // Build from NZ calendar day so the range matches local store time
    let year;
    let month;
    let day;
    try {
      const parts = new Intl.DateTimeFormat('en-NZ', {
        timeZone: 'Pacific/Auckland',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      }).formatToParts(now);
      const get = (type) => parts.find((p) => p.type === type)?.value;
      year = Number(get('year'));
      month = Number(get('month'));
      day = Number(get('day'));
    } catch (e) {
      year = now.getFullYear();
      month = now.getMonth() + 1;
      day = now.getDate();
    }
    const base = new Date(Date.UTC(year, month - 1, day));
    base.setUTCDate(base.getUTCDate() + days);
    return base;
  }

  function initDeliveryEstimate(container) {
    const el = container.querySelector('[data-delivery-estimate]');
    if (!el) return;
    const minDays = Math.max(0, Number(el.getAttribute('data-delivery-min-days')) || 6);
    const maxDays = Math.max(minDays, Number(el.getAttribute('data-delivery-max-days')) || 8);
    const start = formatDeliveryDate(addDaysFromToday(minDays));
    const end = formatDeliveryDate(addDaysFromToday(maxDays));
    el.textContent = `Estimated delivery ${start} to ${end}`;
  }

  function initOnetimeQuantity(container) {
    const input = getOnetimeQtyInput(container);
    const dec = container.querySelector('[data-onetime-qty-dec]');
    const inc = container.querySelector('[data-onetime-qty-inc]');
    if (!input) return;

    function apply(next) {
      setOnetimeQty(container, next, null);
      const variant = getActiveVariant(container);
      if (container._supplementUpdateUI) container._supplementUpdateUI(variant);
      else if (variant) updatePlanQuantityAndTotals(container, variant);
    }

    if (dec) dec.addEventListener('click', () => apply(readOnetimeQty(container) - 1));
    if (inc) inc.addEventListener('click', () => apply(readOnetimeQty(container) + 1));
    input.addEventListener('change', () => apply(readOnetimeQty(container)));
  }

  function syncSupplementFactsToVariant(sectionId, product, variant) {
    const modal = document.getElementById(`SupplementFacts-${sectionId}`);
    if (!modal) return;

    const count = getSupplyCountFromVariant(product, variant);
    const match = count === 90 ? 'three-months' : 'one-month';
    const tabs = modal.querySelectorAll('[data-facts-tab]');
    const panels = modal.querySelectorAll('[data-facts-panel]');

    tabs.forEach((tab) => {
      const visible = tab.getAttribute('data-facts-tab') === match;
      tab.hidden = !visible;
      tab.setAttribute('aria-selected', visible ? 'true' : 'false');
      tab.tabIndex = visible ? 0 : -1;
    });
    panels.forEach((panel) => {
      panel.hidden = panel.getAttribute('data-facts-panel') !== match;
    });
  }

  function initSupplementFactsModal(modal) {
    if (!modal || modal.dataset.factsBound === 'true') return;
    modal.dataset.factsBound = 'true';

    const tabs = modal.querySelectorAll('[data-facts-tab]');
    const panels = modal.querySelectorAll('[data-facts-panel]');

    function activate(name) {
      tabs.forEach((tab) => {
        const selected = tab.getAttribute('data-facts-tab') === name;
        tab.setAttribute('aria-selected', selected ? 'true' : 'false');
        tab.tabIndex = selected ? 0 : -1;
      });
      panels.forEach((panel) => {
        panel.hidden = panel.getAttribute('data-facts-panel') !== name;
      });
    }

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activate(tab.getAttribute('data-facts-tab')));
    });

    modal.querySelectorAll('[data-facts-slider]').forEach((slider) => {
      const slides = Array.from(slider.querySelectorAll('[data-facts-slide]'));
      if (slides.length < 2) return;
      let index = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));

      function show(next) {
        index = (next + slides.length) % slides.length;
        slides.forEach((slide, slideIndex) => {
          slide.classList.toggle('is-active', slideIndex === index);
        });
      }

      const prev = slider.querySelector('[data-facts-prev]');
      const next = slider.querySelector('[data-facts-next]');
      if (prev) prev.addEventListener('click', () => show(index - 1));
      if (next) next.addEventListener('click', () => show(index + 1));
    });
  }

  function initSupplementFactsModals() {
    document.querySelectorAll('[data-supplement-facts-modal]').forEach(initSupplementFactsModal);
  }

  function init(container) {
    if (!container || container.dataset.sectionId === undefined) return;
    initGallery(container);
    initVariantSync(container);
    initPlanCards(container);
    initPurchaseOptions(container);
    initOnetimeQuantity(container);
    initDeliveryEstimate(container);
    initSupplementFactsModals();
    if (getSelectedPurchaseType(container) === 'subscribe') {
      setPackSizeLocked(container, true);
    } else {
      setPackSizeLocked(container, false);
    }
  }

  document.querySelectorAll('supplement-product-info').forEach(init);

  document.addEventListener('shopify:section:load', (e) => {
    const section = e.detail?.sectionId;
    const container = document.querySelector(`supplement-product-info[data-section-id="${section}"]`);
    if (container) init(container);
    else initSupplementFactsModals();
  });

  document.addEventListener('shopify:section:unload', (e) => {
    const sectionId = e.detail?.sectionId;
    if (!sectionId) return;
    const modal = document.getElementById(`SupplementFacts-${sectionId}`);
    if (modal) modal.remove();
  });
})();
